import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

async function apiGet(url) {
    const r = await fetch(url, { credentials: "include" });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.message || "Request failed");
    return data;
}
async function apiPost(url, body) {
    const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.message || "Request failed");
    return data;
}

const FALLBACK_100 = Array.from({ length: 100 }, (_, i) => ({
    no: i + 1,
    name: `Bài ${i + 1}`,
    url: "",
}));

export default function AdminDrumLessons() {
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [items, setItems] = useState(FALLBACK_100);
    const [editing, setEditing] = useState({ no: 1, name: "Bài 1", url: "" });
    const [bulkText, setBulkText] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiGet("/api/drum-lessons");
            const rows = data?.items || [];
            const map = new Map(rows.map((x) => [Number(x.no), x]));
            const merged = FALLBACK_100.map((x) =>
                map.get(x.no) ? { ...x, ...map.get(x.no), url: map.get(x.no)?.url || "" } : x
            );
            setItems(merged);

            // giữ logic chọn bài đang edit
            const cur = merged.find((x) => x.no === editing.no) || merged[0];
            setEditing({ no: cur.no, name: cur.name, url: cur.url || "" });
        } catch (e) {
            toast.error("Không tải được danh sách bài học");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const kw = (q || "").trim().toLowerCase();
        if (!kw) return items;
        return items.filter(
            (x) => String(x.no).includes(kw) || (x.name || "").toLowerCase().includes(kw)
        );
    }, [q, items]);

    function pickRow(x) {
        setEditing({ no: x.no, name: x.name || `Bài ${x.no}`, url: x.url || "" });
    }

    async function saveOne() {
        try {
            await apiPost("/api/drum-lessons/upsert", editing);
            toast.success("Đã lưu");
            await load();
        } catch (e) {
            toast.error("Lưu thất bại (kiểm tra quyền drum_lessons)");
        }
    }

    function parseBulk(text) {
        // mỗi dòng: no|name|url  hoặc no|url
        const lines = (text || "").split("\n").map((s) => s.trim()).filter(Boolean);
        const out = [];
        for (const line of lines) {
            const parts = line.split("|").map((s) => s.trim());
            const no = Number(parts[0]);
            if (!Number.isInteger(no) || no < 1 || no > 100) continue;

            if (parts.length === 2) out.push({ no, name: `Bài ${no}`, url: parts[1] });
            else out.push({ no, name: parts[1] || `Bài ${no}`, url: parts[2] || "" });
        }
        return out;
    }

    async function saveBulk() {
        const parsed = parseBulk(bulkText);
        if (!parsed.length) return toast.error("Không có dòng hợp lệ. Ví dụ: 1|Bài 1|https://...");
        try {
            await apiPost("/api/drum-lessons/bulk-upsert", { items: parsed });
            toast.success(`Đã cập nhật ${parsed.length} dòng`);
            setBulkText("");
            await load();
        } catch {
            toast.error("Bulk lưu thất bại (kiểm tra quyền drum_lessons)");
        }
    }

    return (
        <div className="mx-auto w-full max-w-6xl px-3 py-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h1 className="text-lg font-bold text-slate-800">Quản lý link Trống lục lạc</h1>
                        <p className="text-sm text-slate-500">
                            Chỉ admin hoặc user có quyền <b>drum_lessons</b> mới lưu được.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                    >
                        Tải lại
                    </button>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="md:col-span-2 rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center gap-2">
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Tìm bài (số hoặc tên)…"
                                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
                            />
                            <div className="text-sm text-slate-500 whitespace-nowrap">{filtered.length}/100</div>
                        </div>

                        <div className="mt-3 max-h-[420px] overflow-auto rounded-xl border border-slate-200">
                            {loading ? (
                                <div className="p-3 text-sm text-slate-500">Đang tải…</div>
                            ) : (
                                filtered.map((x) => (
                                    <button
                                        key={x.no}
                                        onClick={() => pickRow(x)}
                                        className={[
                                            "w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50",
                                            x.no === editing.no ? "bg-rose-50" : "bg-white",
                                        ].join(" ")}
                                    >
                                        <div className="text-sm font-semibold text-slate-800">
                                            {x.no}. <span className="font-medium">{x.name}</span>
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">{x.url || "(chưa có link)"}</div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-3">
                        <div className="text-sm font-bold text-slate-800">Sửa nhanh 1 bài</div>

                        <div className="mt-3">
                            <label className="text-xs text-slate-500">Số bài (1..100)</label>
                            <input
                                type="number"
                                min={1}
                                max={100}
                                value={editing.no}
                                onChange={(e) => {
                                    const no = Math.max(1, Math.min(100, Number(e.target.value || 1)));
                                    const cur = items.find((x) => x.no === no) || { no, name: `Bài ${no}`, url: "" };
                                    setEditing({ no, name: cur.name, url: cur.url || "" });
                                }}
                                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
                            />
                        </div>

                        <div className="mt-3">
                            <label className="text-xs text-slate-500">Tên bài</label>
                            <input
                                value={editing.name}
                                onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
                            />
                        </div>

                        <div className="mt-3">
                            <label className="text-xs text-slate-500">Link YouTube</label>
                            <input
                                value={editing.url}
                                onChange={(e) => setEditing((s) => ({ ...s, url: e.target.value }))}
                                placeholder="https://www.youtube.com/watch?v=…"
                                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
                            />
                        </div>

                        <button
                            onClick={saveOne}
                            className="mt-4 w-full rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white hover:bg-rose-700"
                        >
                            Lưu
                        </button>

                        <div className="mt-5 text-sm font-bold text-slate-800">Bulk paste</div>
                        <div className="mt-2 text-xs text-slate-500">Mỗi dòng: <b>no|name|url</b> hoặc <b>no|url</b></div>
                        <textarea
                            value={bulkText}
                            onChange={(e) => setBulkText(e.target.value)}
                            rows={6}
                            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
                            placeholder={"1|Bài 1|https://...\n2|https://..."}
                        />
                        <button
                            onClick={saveBulk}
                            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                        >
                            Cập nhật hàng loạt
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
